'use client';

import * as Sentry from '@sentry/nextjs';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { Alert } from '@//components/Alert';
import { Button } from '@//components/Button';
import { Container } from '@//components/Container';
import { Heading } from '@//components/Heading';
import { PageContent } from '../components/PageContent';
import styles from './error/ErrorPage.module.scss';

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

const ErrorPage = ({ error, reset }: ErrorPageProps) => {
  const t = useTranslations('other.error');

  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', error);
    Sentry.captureException(error);
  }, [error]);

  const headingId = 'error-heading';
  const descriptionId = 'error-description';

  return (
    <Container>
      <PageContent pageType="contentPage">
        <section
          aria-labelledby={headingId}
          aria-describedby={descriptionId}
          className={styles.section}
        >
          <div className={styles.content}>
            <Heading level={1} size="4xl" font="brand" id={headingId}>
              {t('title')}
            </Heading>
            <p className={styles.description} id={descriptionId}>
              {t('description')}
            </p>
          </div>
          <Button variant="primary" size="lg" onClick={reset} title={t('reloadPage')}>
            {t('reloadPage')}
          </Button>
        </section>

        {process.env.NODE_ENV === 'development' && (
          <section className={styles.section} aria-labelledby="error-details-alert">
            <Alert
              id="error-details-alert"
              variant="critical"
              heading="Technical details"
              headingLevel={2}
              role="alert"
            >
              {error.message}
              {error.digest && (
                <span style={{ display: 'block', marginTop: '0.5rem' }}>
                  <strong>Error ID:</strong> {error.digest}
                </span>
              )}
            </Alert>
            {error.stack && (
              <section className={styles.section} aria-labelledby="error-details">
                <details>
                  <summary className={styles.summary} id="error-details">
                    Show stack trace
                  </summary>
                  <pre className={styles.code}>{error.stack}</pre>
                </details>
              </section>
            )}
          </section>
        )}
      </PageContent>
    </Container>
  );
};

export default ErrorPage;
